import { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { CONTACT } from "@/data/site";

const LINKS = [
  { label: "About", id: "about" },
  { label: "Services", id: "services" },
  { label: "Projects", id: "projects" },
  { label: "Reviews", id: "testimonials" },
  { label: "Contact", id: "contact" },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl shadow-sm border-b border-slate-100"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
        <button
          data-testid="nav-logo"
          onClick={() => scrollTo("hero")}
          className="flex items-center gap-3"
        >
          <span className="relative inline-flex h-14 w-14 items-center justify-center">
            <span className="absolute inset-0 rounded-xl ring-2 ring-[#1656b8]/70 animate-ping opacity-60" />
            <span className="absolute inset-0 rounded-xl ring-2 ring-[#1656b8]" />
            <img
              src={CONTACT.logo}
              alt="CWood Construction logo"
              className="relative h-14 w-14 rounded-xl object-cover bg-white p-0.5 shadow-md shadow-[#1656b8]/30 transition-transform duration-300 hover:scale-105"
            />
          </span>
          <span
            className={`font-heading font-extrabold text-lg tracking-tight transition-colors ${
              scrolled ? "text-slate-900" : "text-white"
            }`}
          >
            CWood<span className="text-[#1656b8]">.</span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className={`font-medium text-sm transition-colors hover:text-[#1656b8] ${
                scrolled ? "text-slate-700" : "text-white/90"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-4">
          <a
            data-testid="nav-call-button"
            href={`tel:${CONTACT.phoneTel}`}
            className="flex items-center gap-2 bg-[#1656b8] text-white rounded-lg px-5 py-3 font-semibold text-sm hover:bg-[#0f3d83] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
          >
            <Phone size={16} />
            {CONTACT.phoneDisplay}
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className={`lg:hidden p-2 ${scrolled ? "text-slate-900" : "text-white"}`}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-100 px-4 py-6 flex flex-col gap-4 shadow-lg">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-nav-link-${l.id}`}
              onClick={() => handleNav(l.id)}
              className="text-left font-medium text-slate-700 py-2"
            >
              {l.label}
            </button>
          ))}
          <a
            href={`tel:${CONTACT.phoneTel}`}
            data-testid="mobile-nav-call"
            className="flex items-center justify-center gap-2 bg-[#1656b8] text-white rounded-lg px-5 py-3 font-semibold mt-2"
          >
            <Phone size={16} /> Call {CONTACT.phoneDisplay}
          </a>
        </div>
      )}
    </header>
  );
};
